"use client";

import posthog from "posthog-js";
import { FC, useCallback, useEffect, useRef } from "react";
import ConfirmAction from "./ConfirmAction";

export const TRUSTPILOT_REVIEW_URL =
  "https://www.trustpilot.com/evaluate/klap.app";
export const TRUSTPILOT_REVIEW_PROMPT_QUERY_PARAM = "trustpilotReviewPrompt";
export const TRUSTPILOT_REVIEW_PROMPT_STORAGE_KEY =
  "klap_trustpilot_review_prompt";
export const TRUSTPILOT_REVIEW_PROMPT_DISMISS_COOLDOWN_MS =
  30 * 24 * 60 * 60 * 1000;

type TrustpilotReviewDialogProps = {
  onClose: () => void;
  onReviewClick?: () => void;
  onDismiss?: () => void;
};

const TrustpilotReviewDialog: FC<TrustpilotReviewDialogProps> = ({
  onClose,
  onReviewClick,
  onDismiss,
}) => {
  const actionTakenRef = useRef(false);

  useEffect(() => {
    posthog?.capture("Trustpilot Review Dialog Shown");

    return () => {
      if (actionTakenRef.current) return;
      posthog?.capture("Trustpilot Review Dialog Dismissed");
      onDismiss?.();
    };
  }, [onDismiss]);

  const handleReviewClick = useCallback(() => {
    actionTakenRef.current = true;
    posthog?.capture("Trustpilot Review Dialog CTA Clicked");
    onReviewClick?.();
    window.open(TRUSTPILOT_REVIEW_URL, "_blank", "noopener,noreferrer");
    onClose();
  }, [onClose, onReviewClick]);

  const handleDismiss = useCallback(() => {
    actionTakenRef.current = true;
    posthog?.capture("Trustpilot Review Dialog Dismissed");
    onDismiss?.();
    onClose();
  }, [onClose, onDismiss]);

  return (
    <ConfirmAction
      title="Enjoying Klap?"
      description="We'd really appreciate an honest review on Trustpilot."
      confirmLabel="Review us on Trustpilot"
      cancelLabel="Maybe later"
      caption="It only takes a minute and helps other creators discover Klap."
      onConfirm={handleReviewClick}
      onCancel={handleDismiss}
    />
  );
};

export default TrustpilotReviewDialog;
